import { PinnedFieldsVisibility } from "@mapmycustomers/shared";
import { FieldDescription } from "@mapmycustomers/shared/types/entity";
import Team from "@mapmycustomers/shared/types/Team";

import { displayOrderComparator } from "@app/util/sorting";

import { ENTITY_TYPES_SUPPORTING_PINNED_FIELDS, SOURCE_OBJECT_ENTITY_TYPES } from "../constants";
import EntityColumnsConfiguration, {
  InternalFieldDescription,
} from "../EntityColumnsConfiguration";
import { ListRef } from "../ListRef";

export interface LevelPinnedList {
  fields: FieldDescription[];
  ref: ListRef;
}

// What one level (org, a team or the user) stores. Only what the configuration holds is listed, so
// a list or flag that is absent can be told apart from one that is empty.
export interface LevelPinnedLists {
  actions?: boolean;
  engagement?: boolean;
  lists: LevelPinnedList[];
}

type PinnedFlag = EntityColumnsConfiguration["actions"];

const getLevelPinnedLists = (
  configuration: Partial<EntityColumnsConfiguration>,
  pinnedFieldVisibility: PinnedFieldsVisibility,
  teamId?: Team["id"]
): LevelPinnedLists => {
  const isAtLevel = ({ teamIds, visibility }: Pick<PinnedFlag, "teamIds" | "visibility">) =>
    visibility === pinnedFieldVisibility && (!teamId || (teamIds?.includes(teamId) ?? false));

  const projectFields = (fields: InternalFieldDescription[]): FieldDescription[] =>
    fields
      .filter(isAtLevel)
      .map(({ displayOrder, field }) => ({ displayOrder, fieldName: field.name }))
      .sort(displayOrderComparator);

  const sharedLists = ENTITY_TYPES_SUPPORTING_PINNED_FIELDS.flatMap((entityType) => {
    const fields = configuration[entityType];
    return fields
      ? [{ fields: projectFields(fields), ref: { entityType, kind: "shared" } as const }]
      : [];
  });

  const subtypeLists = SOURCE_OBJECT_ENTITY_TYPES.flatMap((entityType) =>
    (configuration.subtypeLists?.[entityType] ?? []).map(({ fields, key }) => ({
      fields: projectFields(fields),
      ref: { entityType, key, kind: "subtype" } as const,
    }))
  );

  const { actions, engagement } = configuration;

  return {
    ...(actions && { actions: isAtLevel(actions) }),
    ...(engagement && { engagement: isAtLevel(engagement) }),
    lists: [...sharedLists, ...subtypeLists],
  };
};

export default getLevelPinnedLists;
